/**
 * PojistovnaEditModal — edit one insurer (per-section modal). Create is the full-page `PojistovnaForm`; both share `PojistovnaFormBody`.
 * PUTs a slice-only payload (the name / threshold if changed, the tarify whose rates changed).
 */

import { Button } from '@/components/Button';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useTarifyQuery, useUpdatePojistovnaMutation, type PujcovnaPojistovna } from './api';
import { POJISTOVNA_FORM_ID, PojistovnaFormBody, pojistovnaSeed, pojistovnaToBody } from './pojistovna-section-forms';

interface PojistovnaEditModalProps {
  pojistovna: PujcovnaPojistovna;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PojistovnaEditModal({ pojistovna, open, onOpenChange }: PojistovnaEditModalProps) {
  const update = useUpdatePojistovnaMutation(pojistovna.id);
  const tarify = useTarifyQuery();
  // the dialog cannot be dismissed while the save is in flight (its toasts hang on this observer)
  const guarded = (next: boolean) => {
    if (!next && update.isPending) return;
    onOpenChange(next);
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={760}>
      <DialogCrown
        eyebrow="Pojišťovna"
        title={
          <>
            Upravit <em>{pojistovna.nazev}</em>
          </>
        }
        sub="Název, práh dní a denní ceny za náhradní vozidlo podle tarifu vozu."
      />
      <Modal.Body className="px-8 py-5">
        {tarify.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : tarify.isError ? (
          <p className="text-[13px] text-ink-2">Tarify se nepodařilo načíst, pojišťovnu teď nelze upravit.</p>
        ) : (
          <PojistovnaFormBody
            initial={pojistovnaSeed(tarify.data ?? [], pojistovna)}
            tarify={tarify.data ?? []}
            busy={update.isPending}
            onSubmit={(form) => {
              if (!form.nazev.trim()) {
                toast.error('Zadejte název pojišťovny.');
                return;
              }
              const body = pojistovnaToBody(form, tarify.data ?? [], pojistovna);
              if (!body.ok) {
                toast.error(body.chyba);
                return;
              }
              update.mutate(body.body, {
                onSuccess: () => {
                  toast.ok('Pojišťovna uložena.');
                  onOpenChange(false);
                },
                onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit pojišťovnu.'}`),
              });
            }}
          />
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button variant="secondary" onClick={() => guarded(false)} disabled={update.isPending}>
              Zrušit
            </Button>
            <Button variant="ink" type="submit" form={POJISTOVNA_FORM_ID} disabled={update.isPending || tarify.isLoading || tarify.isError}>
              Uložit
            </Button>
          </>
        }
      />
    </Modal>
  );
}
